import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { ApBaseModalComponent, ApNotificationService } from 'webcommon/core-ui';
import { MacroContextEnum, MacroDataSourceEnum, MacroDefinitionDto, MacroGroupDto, SystemGuid } from 'webcommon/shared';

import { MacroRepository, MacroService, PatientMacroRepository } from '../ap-macro/index';

import { Observable, iif } from 'rxjs';
import { MacroInterfaces } from '../ap-macro/MacroInterfaces';
import { ApMacroEditorComponent } from './ap-macro-editor.component';

@Component({
  selector: 'ap-macro-editor-modal',
  templateUrl: './ap-macro-editor-modal.component.html',
})
export class ApMacroEditorModalComponent extends ApBaseModalComponent<boolean> implements OnInit {
  @ViewChild('macroEditor', { static: true }) macroEditor: ApMacroEditorComponent;

  action: string;
  macroService: MacroService;
  baseId: string;
  hasWarning = false;
  macroDefinitionDto: MacroDefinitionDto;
  macroInterfaces: MacroInterfaces;
  saving = false;
  title: string;
  defaultMacroGroup: MacroGroupDto | null = null;
  warningText: string;
  allowedMacroGroups: string[];

  Id: string;

  origMacroName: string | null;
  originalMacroDefinitionDtoValue: MacroDefinitionDto;

  constructor(
    elementInjector: Injector,
    private patientMacroRepository: PatientMacroRepository,
    private apNotificationService: ApNotificationService,
    private systemGuid: SystemGuid,
    private macroRepository: MacroRepository,
  ) {
    super(elementInjector);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.setId();

    if (this.macroDefinitionDto) {
      this.origMacroName = this.macroDefinitionDto.Name;
    }

    this.originalMacroDefinitionDtoValue = { ...this.macroDefinitionDto };
  }

  closeWarning() {
    this.hasWarning = false;
    this.warningText = '';
  }

  showWarning(): boolean {
    return this.hasWarning;
  }

  close() {
    if (this.saving) {
      return;
    }
    this.saving = true;
    if (!this.isMacroValidToSave()) {
      this.saving = false;
      return;
    }
    const request$ = this.saveMacroBasedOnMacroType();
    request$.subscribe((result) => {
      Object.assign(this.macroDefinitionDto, this.originalMacroDefinitionDtoValue);
      this.saveComplete(result);
      super.closeModal(false);
    },
      (error: unknown) => {
        this.hasWarning = true;
        this.warningText = 'Failed to ' + this.action + ' macro ' + this.originalMacroDefinitionDtoValue.Name + ' !'
          + '<br />' + this.getErrorTextFromResponse(error);
      },
    );
  }

  private saveMacroBasedOnMacroType(): Observable<MacroDefinitionDto> {
    this.originalMacroDefinitionDtoValue.DataSource = MacroDataSourceEnum.Client;
    this.originalMacroDefinitionDtoValue.DataContext = MacroContextEnum.None;
    const patientId = this.macroService.getPatientId();

    // Create observable base on macro is a patient macro or macro without a patient

    const request$ = iif(
      () => this.originalMacroDefinitionDtoValue.IsPatientRequired,
      this.savePatientMacro(patientId),
      this.saveMacroWithoutPatient()
    );
    return request$;
  }

  private savePatientMacro(patientId: string | null): Observable<MacroDefinitionDto> {
    const savePatientMacro$: Observable<MacroDefinitionDto> =
      this.patientMacroRepository.saveMacro(patientId ?? this.systemGuid.GuidEmpty, this.originalMacroDefinitionDtoValue).pipe(
        finalize(() => {
          this.saving = false;
        }),
        this.takeUntilDestroy()
      );
    return savePatientMacro$;
  }

  private saveMacroWithoutPatient(): Observable<MacroDefinitionDto> {
    const saveMacro$: Observable<MacroDefinitionDto> =
      this.macroRepository.saveMacro(this.originalMacroDefinitionDtoValue).pipe(
        finalize(() => {
          this.saving = false;
        }),
        this.takeUntilDestroy()
      );
    return saveMacro$;
  }

  public saveComplete(macroDefinitionDto: MacroDefinitionDto) {
    if (!macroDefinitionDto) { return; }
    if (!this.originalMacroDefinitionDtoValue.Id) {
      this.macroService.addMacroDefinitionDto(macroDefinitionDto);
    } else {
      this.macroDefinitionDto.IsVisitRequired = macroDefinitionDto.IsVisitRequired;
      this.macroDefinitionDto.IsPatientRequired = macroDefinitionDto.IsPatientRequired;
      this.macroService.onMacroListHasChanged();
    }
    this.showNotification(macroDefinitionDto.Name, this.origMacroName);
  }

  private showNotification(name: string | null, origMacroName: string | null) {
    if (this.action === 'create') {
      this.apNotificationService.success('Macro ' + name + ' created', '', 3000);
      return;
    }
    if (this.action === 'modify') {
      if (origMacroName !== name) {
        this.apNotificationService.success('Macro ' + origMacroName + ' renamed to ' + name, '', 3000);
        return;
      }
      this.apNotificationService.success('Macro ' + name + ' modified', '', 3000);
      return;
    }
    this.apNotificationService.success('Macro ' + name + ' cloned', '', 3000);
  }

  private isMacroValidToSave(): boolean {
    if (!this.macroEditor.isAllInputValid()) {
      return false;
    }
    if (!this.isNameValid()) {
      return false;
    }
    if (!this.isSaveAllowed()) {
      return false;
    }
    return true;
  }

  private isNameValid() {
    if (this.originalMacroDefinitionDtoValue.Name) {
      const match = this.originalMacroDefinitionDtoValue.Name.match('^[a-zA-Z][a-zA-Z0-9-_]+$');
      if (match && match.length > 0) {
        return true;
      } else {
        this.hasWarning = true;
        this.warningText =
          'Invalid Macro Name! <br /> The Name must start with an letter followed by alphanumeric characters, underscores or dashes.';
      }
    } else {
      this.hasWarning = true;
      this.warningText = 'Unable to ' + this.action + ' macro! <br /> A Name must be specified.';
    }
    return false;
  }

  private isSaveAllowed() {
    if (!this.macroService.isSaveAllowed(this.originalMacroDefinitionDtoValue)) {
      this.hasWarning = true;
      this.warningText =
        'Unable to ' + this.action + ' macro! <br /> A macro named ' + this.originalMacroDefinitionDtoValue.Name + ' is already defined';
      return false;
    }

    if (!this.allowedMacroGroups.includes(this.originalMacroDefinitionDtoValue.MacroGroup.MacroGroupId.toLowerCase())) {
      this.hasWarning = true;
      this.warningText =
        `Unable to ${this.action} macro! <br /> User does not have permission to ${this.action} macros for selected macro group`;
      return false;
    }

    return true;
  }

  private getErrorTextFromResponse(error: any): string {
    const errMsg = error.error.ExceptionMessage ? error.error.ExceptionMessage
      : this.isResultError(error) ? this.getResultText(error)
        : error.status ? `${error.status} - ${error.statusText}` : 'Server error';
    return errMsg;
  }

  private isResultError(error: any): boolean {
    const x = error.error.ModelState.commandResult;
    return (x);
  }

  private getResultText(error: any): string {
    return error.error.ModelState.commandResult[0];

  }

  private setId() {
    this.Id = `${this.baseId}-macro-editor-modal`;
  }
}
